import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  clearTokens,
  getUserFromToken,
  isAuthenticated,
  getAccessToken,
  getTimeUntilExpiry,
  setTokens,
  getRefreshToken,
} from '../utils/auth'
import { apolloClient } from '../graphql/client'
import {
  RefreshAccessTokenDocument,
  RefreshAccessTokenMutation,
  RefreshAccessTokenMutationVariables,
  useLogoutMutation,
} from '../graphql/generated/graphql'

interface User {
  userId: string
  email: string
  role: string
  merchantId?: number
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  logout: () => void
  refreshUser: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export { AuthContext }

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

// Store refresh promise globally to prevent duplicate refreshes
let globalRefreshPromise: Promise<void> | null = null

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const scheduleTokenRefreshRef = useRef<(() => Promise<void>) | null>(null)
  const [logoutMutation] = useLogoutMutation()

  const refreshUser = useCallback(() => {
    if (isAuthenticated()) {
      const tokenData = getUserFromToken()
      if (tokenData) {
        setUser({
          userId: tokenData.userId,
          email: tokenData.email,
          role: tokenData.role,
          merchantId: tokenData.merchantId,
        })
      }
    } else {
      setUser(null)
    }
    setIsLoading(false)
  }, [])

  const logout = useCallback(async () => {
    const timestamp = new Date().toISOString()
    try {
      // Call server logout mutation
      await logoutMutation()
    } catch (error) {
      // Even if server logout fails, proceed with local logout
      console.error(`[${timestamp}][AuthContext] Server logout error:`, error)
    } finally {
      // Clear local state regardless of server response
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
      clearTokens()
      setUser(null)
      window.location.href = '/login'
    }
  }, [logoutMutation])

  const performTokenRefresh = useCallback(async () => {
    // Use global promise to prevent duplicate refreshes
    if (globalRefreshPromise) {
      await globalRefreshPromise
      return
    }

    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      logout()
      return
    }

    globalRefreshPromise = (async () => {
      try {
        const { data } = await apolloClient.mutate<
          RefreshAccessTokenMutation,
          RefreshAccessTokenMutationVariables
        >({
          mutation: RefreshAccessTokenDocument,
          variables: { input: { refreshToken } },
        })

        if (
          data?.refreshAccessToken?.accessToken &&
          data.refreshAccessToken.refreshToken
        ) {
          setTokens(
            data.refreshAccessToken.accessToken,
            data.refreshAccessToken.refreshToken
          )
          refreshUser()
          // Schedule next refresh
          if (scheduleTokenRefreshRef.current) {
            await scheduleTokenRefreshRef.current()
          }
        } else {
          logout()
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[AuthContext] Token refresh error:', error)
        }
        logout()
      } finally {
        globalRefreshPromise = null
      }
    })()

    await globalRefreshPromise
  }, [refreshUser, logout])

  // Proactive token refresh
  const scheduleTokenRefresh = useCallback(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) return

    const timeUntilExpiry = getTimeUntilExpiry(accessToken)

    // Clear existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current)
    }

    // Schedule refresh 5 seconds before expiry (or immediately if less than 5 seconds)
    const refreshIn = Math.max(0, (timeUntilExpiry - 5) * 1000)

    if (refreshIn > 0) {
      refreshTimerRef.current = setTimeout(async () => {
        await performTokenRefresh()
      }, refreshIn)
    } else if (timeUntilExpiry > 0) {
      // Token is about to expire, refresh immediately
      await performTokenRefresh()
    }
  }, [performTokenRefresh])

  // Store scheduleTokenRefresh in ref for use in performTokenRefresh
  useEffect(() => {
    scheduleTokenRefreshRef.current = scheduleTokenRefresh
  }, [scheduleTokenRefresh])

  useEffect(() => {
    refreshUser()
    scheduleTokenRefresh()

    // Cleanup on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [refreshUser, scheduleTokenRefresh])

  // Note: sessionStorage changes are NOT shared between tabs/windows
  // This event listener won't fire for sessionStorage changes
  // Each tab/window has its own sessionStorage

  return (
    <AuthContext.Provider value={{ user, isLoading, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  )
}
